import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { EmptyState } from '../empty-state/empty-state.tsx';
import type { ChartSeries } from './palette.ts';

export interface ChartFrameProps {
  /** Reserves the plot area and gives the canvas a box to fill. */
  height: number;
  /** Names the plot for a screen reader; ignored when the frame is empty. */
  ariaLabel: string;
  /** Read out under the canvas; ignored when the frame is empty. */
  summary: string;
  hasData: boolean;
  emptyTitle: ReactNode;
  className?: string;
  children: ReactNode;
}

export function hasChartData(series: ChartSeries[]): boolean {
  return series.some((one) => one.data.some((value) => Number.isFinite(value)));
}

/**
 * chart.js measures its parent, so the canvas needs a box with a definite
 * height; and an empty grid with axes reads as a chart that failed, so the box
 * stands in for it when there is nothing to draw.
 */
export function ChartFrame({
  height,
  ariaLabel,
  summary,
  hasData,
  emptyTitle,
  className,
  children,
}: ChartFrameProps) {
  return (
    <div
      data-slot='chart-frame'
      className={cn('relative w-full', className)}
      style={{ height } satisfies CSSProperties}
    >
      {hasData ? (
        <>
          {/* react-chartjs-2 hard-codes role="img" on the canvas, so the
                name goes on the box and the canvas itself is hidden. */}
          <div
            data-slot='chart-plot'
            role='img'
            aria-label={ariaLabel}
            className='size-full'
          >
            {children}
          </div>
          <p data-slot='chart-summary' className='sr-only'>
            {summary}
          </p>
        </>
      ) : (
        <EmptyState
          data-slot='chart-empty'
          title={emptyTitle}
          className='h-full justify-center'
        />
      )}
    </div>
  );
}
